import { Badge, Checkbox, FooterCell, Stack, Typography } from '@ui-components/neo.tax';
import { useMemo, ReactElement } from 'react';
import { CellProps, Column, type UseRowSelectInstanceProps } from 'react-table';

import { useRdContext } from '@neo.tax/rd/context';
import { RdProject, RdProjectsSummary } from '@neo.tax/rd/hooks';
import {
  getRdCategoryBadgeConfig,
  getAiGuidanceBadgeConfig,
  ProjectBadgeConfig,
  getRdDocumentationStatus,
} from '@neo.tax/rd/utils';
import {
  getPath,
  NavLinkTo,
  Path,
  useCurrentCompanyId,
  TableHeader,
  TABLE_CELL_PLACEHOLDER,
  useHasSkippedConnectingTicketing,
  ProjectLeftBar,
  ProjectName,
  HIGHLIGHT_VARIANT,
} from '@neo.tax/shared';

import { RdDocumentationBadgeConfig } from '../rd-projects-layout.types';
import { getRdDetailsBadgeConfig } from '../rd-projects-layout.utils';
import { RdProjectAIGuidanceCell } from '../RdProjectAIGuidanceCell';

interface UseRdProjectsTableColumnsProps<T extends Record<string, any>> {
  clickedRowId?: string;
  footerData?: RdProjectsSummary | null;
  onSelectAllRows: (checked: boolean) => void;
  onSelectRow: (checked: boolean, item: T) => void;
  rowCount: number;
  selectedRowIds: string[];
  visibleColumns: string[];
}

export const useRdProjectsTableColumns = ({
  clickedRowId,
  footerData,
  onSelectAllRows,
  onSelectRow,
  rowCount,
  selectedRowIds,
  visibleColumns,
}: UseRdProjectsTableColumnsProps<RdProject>): Column<RdProject>[] => {
  const companyId = useCurrentCompanyId();
  const { fiscalYearId } = useRdContext();
  const { hasSkippedConnectingTicketing } = useHasSkippedConnectingTicketing();

  const columns = useMemo<Column<RdProject>[]>(() => {
    const handleSelectRow =
      (cell: CellProps<RdProject, RdProject> & UseRowSelectInstanceProps<RdProject>) =>
      (checked: boolean): void => {
        cell.toggleRowSelected(cell.value.id, checked);
        onSelectRow(checked, cell.value);
      };

    const baseColumns: Column<RdProject>[] = [
      {
        id: 'name',
        accessor: (datum: RdProject): RdProject => datum,
        width: 'minmax(360px, 420px)',
        Header: () => (
          <Stack direction="row">
            <Checkbox
              id="projects"
              name="projects"
              checked={selectedRowIds.length > 0 && selectedRowIds.length === rowCount}
              onChange={onSelectAllRows}
            />
            <TableHeader>Projects</TableHeader>
          </Stack>
        ),
        Cell: (cell: CellProps<RdProject, RdProject> & UseRowSelectInstanceProps<RdProject>): ReactElement => (
          <>
            <ProjectLeftBar $variant={HIGHLIGHT_VARIANT} $isClicked={clickedRowId === cell.value.id} />
            <Checkbox
              id={cell.value.id}
              name={cell.value.name}
              checked={selectedRowIds?.includes(cell.value.id)}
              onChange={handleSelectRow(cell)}
            />
            <ProjectName
              $variant={HIGHLIGHT_VARIANT}
              title={cell.value.name}
              to={getPath({
                path: Path.RD_PROJECTS,
                companyId,
                fiscalYearId,
                rdProjectId: cell.value.id,
              })}
            >
              {cell.value.name}
            </ProjectName>
          </>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.count?.name ?? '0';
          return <FooterCell preText="COUNT" text={value} />;
        },
      },
      {
        id: 'productName',
        width: 180,
        Header: () => <TableHeader>Product</TableHeader>,
        accessor: (datum: RdProject): RdProject['product'] => datum.product,
        Cell: ({ value }: CellProps<RdProject, RdProject['product']>): ReactElement =>
          value ? (
            <Badge colorVariant="yellow" dot={false} maxWidth={150}>
              {value.name}
            </Badge>
          ) : (
            <></>
          ),
      },
      {
        id: 'rdCategory',
        width: 180,
        accessor: (datum: RdProject): ProjectBadgeConfig | undefined => getRdCategoryBadgeConfig(datum),
        Header: () => <TableHeader title="The R&D status you selected in the Project drawer.">R&D Status</TableHeader>,
        Cell: ({ value }: CellProps<RdProject, ProjectBadgeConfig | undefined>): ReactElement =>
          value ? (
            <Badge colorVariant={value.colorVariant} dot={false} title={value.description}>
              {value.label}
            </Badge>
          ) : (
            <></>
          ),
      },
      {
        id: 'aiGuidance',
        width: 180,
        accessor: (datum: RdProject): ProjectBadgeConfig | undefined =>
          getAiGuidanceBadgeConfig(datum.aiGuidance, datum.rdDetermination),
        Header: () => (
          <TableHeader title="Automated guidance on project eligibility for the R&D credit. Only available when you connect ticketing.">
            AI Guidance
          </TableHeader>
        ),
        Cell: ({ row, value }: CellProps<RdProject, ProjectBadgeConfig | undefined>): ReactElement => {
          if (!value) return <></>;
          if (value.label === 'Needs Review') {
            return (
              <NavLinkTo
                to={getPath({
                  path: Path.RD_PROJECTS,
                  companyId,
                  fiscalYearId,
                  rdProjectId: row.original.id,
                })}
                hash="eligibility"
              >
                <RdProjectAIGuidanceCell colorVariant={value.colorVariant} title={value.description}>
                  {value.label}
                </RdProjectAIGuidanceCell>
              </NavLinkTo>
            );
          }
          return (
            <RdProjectAIGuidanceCell colorVariant={value.colorVariant} title={value.description}>
              {value.label}
            </RdProjectAIGuidanceCell>
          );
        },
      },
      {
        id: 'rdDetails',
        width: 130,
        Header: () => <TableHeader>R&D Details</TableHeader>,
        accessor: (datum: RdProject): RdDocumentationBadgeConfig | undefined =>
          getRdDetailsBadgeConfig(datum.consideredForRd, getRdDocumentationStatus(datum)),
        Cell: ({ value }: CellProps<RdProject, RdDocumentationBadgeConfig | undefined>): ReactElement =>
          value ? (
            <Badge colorVariant={value.colorVariant} dot={false} title={value.description}>
              {value.label}
            </Badge>
          ) : (
            <></>
          ),
      },
      {
        id: 'owner',
        width: 150,
        Header: (): ReactElement => <TableHeader textAlign="left">Owner</TableHeader>,
        accessor: (datum: RdProject): string => datum.projectOwner?.fullName ?? '',
        Cell: ({ value }: CellProps<RdProject, string>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit">
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
      },
    ];

    const ticketingColumns: Column<RdProject>[] = hasSkippedConnectingTicketing
      ? []
      : [
          {
            id: 'teams',
            width: 150,
            Header: (): ReactElement => <TableHeader>Teams</TableHeader>,
            accessor: (datum: RdProject): string => datum.workstreamNamesSearch ?? '',
            Cell: ({ row }: CellProps<RdProject, string>): ReactElement => {
              const teams = row.original.workstreamNames ?? [];
              return (
                <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={teams.join(', ')}>
                  {teams.join(', ')}
                </Typography>
              );
            },
          },
          {
            id: 'tickets',
            width: 120,
            Header: (): ReactElement => <TableHeader textAlign="right">Tickets</TableHeader>,
            accessor: (datum: RdProject): number => datum.ticketCount,
            Cell: ({ value }: CellProps<RdProject, number>): ReactElement => (
              <Stack fullWidth justifyContent="end">
                <Typography color="inherit" textOverflow="truncate" fontSize="inherit">
                  {value}
                </Typography>
              </Stack>
            ),
            Footer: (): ReactElement => {
              const average = footerData?.avg?.ticketCount ?? 0;
              return <FooterCell alignText="end" preText="AVG" text={`${Math.round(average)}`} />;
            },
          },
        ];

    const allColumns = [...baseColumns, ...ticketingColumns];

    return allColumns.filter((column) => column.id && visibleColumns.includes(column.id));
  }, [
    hasSkippedConnectingTicketing,
    onSelectRow,
    selectedRowIds,
    rowCount,
    onSelectAllRows,
    clickedRowId,
    companyId,
    fiscalYearId,
    footerData,
    visibleColumns,
  ]);

  return columns;
};
